import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { request, stripTags, URLParams } from 'app/utils';
import { useSchool } from 'features/school';
import { useProducts, ProductDetailsApiResponse } from 'features/products';
import { Scorm, ScormAssessment, ScormPlayer, ScormVersion, useScorm } from 'features/scorm';
import { Loading, Page } from 'components';

const ContentScormView = () => {
  const [loading, setLoading] = useState(true);

  const { productSlug, productClassUuid } = useParams();

  const { apiGatewayUrl, schoolAlias } = useSchool();
  const { productDetails, setProductDetails, clearProductDetails } = useProducts();
  const { scorm, setScorm, clearScorm, scormAssessment, setScormAssessment, clearScormAssessment } = useScorm();

  const [searchParams] = useSearchParams();
  const src = searchParams.get('src') || '';

  const scormVersion = useMemo((): ScormVersion => (scorm?.version.includes('2004') ? '2004' : '1.2'), [scorm?.version]);

  const fetchScorm = useCallback(async () => {
    setLoading(true);

    const params = URLParams({
      src,
    });

    await Promise.all([
      request<ProductDetailsApiResponse>({
        url: `/api/v1/School/Products/${productSlug}?productClassUuid=${productClassUuid}&${params}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then((response) => {
        setProductDetails(response.data);
      }),

      request<Scorm>({
        url: `/api/v1/School/Scorm/${productClassUuid}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then(({ data }) => {
        setScorm(data);
      }),

      request<ScormAssessment>({
        url: `/api/v1/School/Scorm/Assessment/${productClassUuid}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then(({ data }) => {
        setScormAssessment(data);
      }),
    ]).finally(() => {
      setLoading(false);
    });
  }, [productClassUuid, src]);

  useEffect(() => {
    fetchScorm();
  }, [productClassUuid, src]);

  useEffect(() => {
    return () => {
      clearProductDetails();
      clearScorm();
      clearScormAssessment();
    };
  }, []);

  const handleSaveCompletion = useCallback(
    (completionData: string, completionPercentage: number) => {
      request({
        url: `/api/v1/School/Scorm/Assessment`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          request: {
            productClassUuid,
            completionPercentage,
            completionData,
          },
        },
      });
    },
    [productClassUuid]
  );

  if (!scorm || loading) return <Loading label="Por favor, aguarde." />;

  return (
    <Page title={stripTags(productDetails?.product.name)}>
      <ScormPlayer
        scormVersion={scormVersion}
        src={`/scorm_files/${schoolAlias}/${scorm?.uuid}/${scorm?.href}`}
        scormAssessment={scormAssessment}
        onSaveCompletion={handleSaveCompletion}
      />
    </Page>
  );
};

export default ContentScormView;
